/**
 * CommentsPanel.jsx
 * Sprint 4 – Enterprise Comments & Collaboration
 *
 * Slide-over panel (right side, 480 px wide) that replaces DiscussionDrawer.
 *
 * Features:
 *   - Unified comment timeline for task + all its subtasks
 *   - Nested replies (one level deep)
 *   - Subtask badge per comment
 *   - @mention autocomplete (project members only)
 *   - Subtask selector on new comments
 *   - Edit / Delete for senders
 *   - Search by user, text, or @mention
 *   - Read-only banner when task is closed
 *   - Unread count badge (passed in as prop, zeroed on open)
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import CommentItem  from './CommentItem';
import CommentInput from './CommentInput';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

// ─────────────────────────────────────────────────────────────────────────────

export default function CommentsPanel({
  open,
  onClose,
  taskId,
  task,            // full task object (used for read-only check)
  currentUserId,
  token,
  unreadCount = 0,
  onUnreadClear,   // () => void — called after marking read
  lockedSubtask,   // { id, title } — when set (opened from a subtask), the context
                   // is locked to that subtask; selector is hidden
}) {
  const listRef = useRef();
  const token_  = token || localStorage.getItem('jwt') || localStorage.getItem('token');
  // Store onUnreadClear in a ref so loadAll doesn't need it as a dependency.
  // This prevents loadAll (and all handlers that depend on it) from being
  // recreated every render just because the inline arrow fn passed as prop changes.
  const onUnreadClearRef = useRef(onUnreadClear);
  useEffect(() => { onUnreadClearRef.current = onUnreadClear; }, [onUnreadClear]);

  const [comments,  setComments]  = useState([]);
  const [members,   setMembers]   = useState([]);
  const [subtasks,  setSubtasks]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [sending,   setSending]   = useState(false);
  const [search,    setSearch]    = useState('');

  const taskReadOnly = String(task?.status || '').toLowerCase() === 'closed';

  // ── Fetch all data when panel opens ──────────────────────────────────────
  useEffect(() => {
    if (!open || !taskId) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId]);

  // ── Auto-scroll to bottom on new comments ─────────────────────────────────
  useEffect(() => {
    if (open && listRef.current && !loading) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments, open, loading]);

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = authHeaders(token_);
      const [commentsRes, membersRes, subtasksRes] = await Promise.all([
        axios.get(`${API}/tasks/${taskId}/comments`,          { headers }),
        axios.get(`${API}/tasks/${taskId}/comments/members`,  { headers }),
        axios.get(`${API}/tasks/${taskId}/comments/subtasks`, { headers }),
      ]);
      setComments(commentsRes.data  || []);
      setMembers(membersRes.data    || []);
      setSubtasks(subtasksRes.data  || []);

      // Mark as read — use the ref so this doesn't cause loadAll to be recreated
      if (!taskReadOnly) {
        try {
          await axios.patch(`${API}/tasks/${taskId}/comments/read`, {}, { headers });
          onUnreadClearRef.current?.();
        } catch { /* non-fatal */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error loading comments');
    } finally {
      if (!silent) setLoading(false);
    }
    // onUnreadClear is intentionally excluded — stored in a ref to avoid recreating loadAll
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, token_, taskReadOnly]);

  // Expose refresh so parent can call after SSE event
  CommentsPanel._refresh = loadAll;

  // ── Post top-level comment ────────────────────────────────────────────────
  const handleSend = async ({ message, subtask_id, mentions }) => {
    setSending(true);
    const effectiveSubtaskId = lockedSubtask ? lockedSubtask.id : subtask_id;
    try {
      await axios.post(
        `${API}/tasks/${taskId}/comments`,
        { message, subtask_id: effectiveSubtaskId, mentions },
        { headers: authHeaders(token_) }
      );
      // Silent refresh — no loading spinner so comment list stays visible
      await loadAll(true);
      toast.success('Comment posted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error posting comment');
    } finally {
      setSending(false);
    }
  };

  const handleReply = useCallback(async (parentId, { message, mentions }) => {
    try {
      await axios.post(
        `${API}/tasks/${taskId}/comments/${parentId}/reply`,
        { message, mentions },
        { headers: authHeaders(token_) }
      );
      toast.success('Reply posted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error posting reply');
    }
    await loadAll(true);
  }, [taskId, token_, loadAll]);

  // ── Edit comment ──────────────────────────────────────────────────────────
  const handleEdit = useCallback(async (commentId, newMessage) => {
    try {
      await axios.put(
        `${API}/tasks/${taskId}/comments/${commentId}`,
        { message: newMessage },
        { headers: authHeaders(token_) }
      );
      toast.success('Comment updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error updating comment');
    }
    await loadAll(true);
  }, [taskId, token_, loadAll]);

  // ── Delete comment ────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (commentId) => {
    try {
      await axios.delete(
        `${API}/tasks/${taskId}/comments/${commentId}`,
        { headers: authHeaders(token_) }
      );
      toast.success('Comment deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error deleting comment');
    }
    await loadAll(true);
  }, [taskId, token_, loadAll]);

  // ── Search filtering ──────────────────────────────────────────────────────
  const q = search.trim().toLowerCase();

  function commentMatchesSearch(c) {
    if (!q) return true;
    const inMessage = (c.message || '').toLowerCase().includes(q);
    const inAuthor  = (c.author_name || '').toLowerCase().includes(q);
    const inMentions = (c.mentions || []).some(id => {
      const m = members.find(mem => mem.id === id);
      return m && `${m.first_name} ${m.last_name}`.toLowerCase().includes(q);
    });
    const inSubtask = (c.subtask_title || '').toLowerCase().includes(q);
    // also check replies
    const inReplies = (c.replies || []).some(r =>
      (r.message || '').toLowerCase().includes(q) ||
      (r.author_name || '').toLowerCase().includes(q)
    );
    return inMessage || inAuthor || inMentions || inSubtask || inReplies;
  }

  const visibleComments = q ? comments.filter(commentMatchesSearch) : comments;

  // Count total including replies for display
  const totalCount = comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="absolute right-0 top-0 h-full bg-white shadow-2xl pointer-events-auto flex flex-col" style={{ width: 480 }}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-gray-900">Comments</span>
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                {totalCount}
              </span>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold">
                  {unreadCount} new
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {taskReadOnly
                ? '🔒 Task closed — comments are read-only'
                : lockedSubtask
                ? `Commenting on subtask: ${lockedSubtask.title}`
                : 'Unified comments for this task and all its subtasks'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition text-lg"
            aria-label="Close comments"
          >
            ✕
          </button>
        </div>

        {/* ── Search bar ── */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by user, text, or @mention…"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-3 py-1.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:bg-white transition"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
            )}
          </div>
          {q && (
            <p className="text-xs text-gray-400 mt-1">
              {visibleComments.length === 0 ? 'No comments match.' : `${visibleComments.length} thread${visibleComments.length !== 1 ? 's' : ''} matching "${search}"`}
            </p>
          )}
        </div>

        {/* ── Read-only banner ── */}
        {taskReadOnly && (
          <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            <p className="text-xs text-gray-500 font-medium text-center">
              🔒 This task is <strong>Closed</strong>. The comment history is preserved and read-only.
            </p>
          </div>
        )}

        {/* ── Comment list ── */}
        <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <div className="w-7 h-7 border-2 border-[#7AA33E] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Loading comments…</p>
            </div>
          ) : visibleComments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              <span className="text-5xl">💬</span>
              <p className="text-gray-500 font-medium text-sm">
                {q ? 'No comments match your search.' : 'No comments yet.'}
              </p>
              {!q && !taskReadOnly && (
                <p className="text-gray-400 text-xs">Be the first to start the conversation.</p>
              )}
            </div>
          ) : (
            <div className="space-y-0.5">
              {visibleComments.map(comment => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  currentUserId={currentUserId}
                  taskReadOnly={taskReadOnly}
                  members={members}
                  subtasks={subtasks}
                  searchQuery={search}
                  onReply={handleReply}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Input area ── */}
        {taskReadOnly ? (
          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 text-center text-xs text-gray-400 font-medium flex-shrink-0">
            🔒 Task Closed — Comments are read-only
          </div>
        ) : (
          <div className="flex-shrink-0 border-t border-gray-200">
            <CommentInput
              onSend={handleSend}
              sending={sending}
              members={members}
              subtasks={subtasks}
              lockedSubtask={lockedSubtask || null}
            />
          </div>
        )}
      </div>
    </div>
  );
}
